<template>
  <!-- Embedded mode (?embedded=1) — render slot only, tanpa sidebar/header.
       Dipakai saat halaman dimuat di iframe popup (mis. Simulation Window di
       Production Planning) supaya tampilannya bersih. -->
  <div v-if="embedded" class="min-h-screen bg-slate-50 p-3 md:p-6">
    <slot />
  </div>
  <div v-else class="min-h-screen flex bg-slate-50">

    <!-- Mobile backdrop — di-show saat drawer dibuka pada layar < md -->
    <div
      v-if="sidebarOpenMobile"
      class="md:hidden fixed inset-0 bg-black/40 z-30"
      @click="sidebarOpenMobile = false"
    />

    <!-- Sidebar — di-hide saat floating window di-pin ke kiri layar. -->
    <aside
      v-if="!pinnedFloatingWindow"
      class="bg-white border-r border-gray-100 flex flex-col fixed h-full z-40 shadow-sm transition-[transform,width] duration-200 w-60"
      :class="[
        sidebarOpenMobile ? 'translate-x-0' : '-translate-x-full md:translate-x-0',
        sidebarCollapsed ? 'md:w-16' : 'md:w-60',
      ]"
    >
      <!-- Logo + toggle -->
      <div class="h-12 md:h-14 flex items-center border-b border-gray-100 flex-shrink-0 px-3 gap-2.5">
        <NuxtLink
          to="/"
          class="flex items-center gap-2.5 min-w-0 flex-1 rounded-lg hover:bg-gray-50 -mx-1 px-1 py-1 transition-colors"
          title="Back to home"
        >
          <img src="/salestial-logo.png" alt="Salestial" class="w-8 h-8 flex-shrink-0" />

          <div v-if="!sidebarCollapsed" class="flex-1 min-w-0 overflow-hidden">
            <p class="text-sm font-bold text-gray-900 leading-tight whitespace-nowrap">{{ moduleLabel.short }}</p>
            <p class="text-[11px] text-gray-400 leading-tight whitespace-nowrap">{{ moduleLabel.long }}</p>
          </div>
        </NuxtLink>

        <!-- Mobile close (X) -->
        <button
          class="ml-auto md:hidden w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0"
          title="Close menu"
          @click="sidebarOpenMobile = false"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>

        <!-- Desktop collapse: only visible when expanded & ≥ md -->
        <button
          v-if="!sidebarCollapsed"
          class="hidden md:flex ml-auto w-7 h-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors flex-shrink-0"
          title="Collapse sidebar"
          @click="sidebarCollapsed = true"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
          </svg>
        </button>
      </div>

      <!-- Nav -->
      <div class="flex-1 overflow-y-auto overflow-x-hidden py-3 space-y-0.5" :class="sidebarCollapsed ? 'px-2' : 'px-3'">

        <p v-if="!sidebarCollapsed" class="px-3 pt-1 pb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">Analytics</p>
        <div v-else class="h-2" />

        <NavIcon to="/dashboard"   icon="layout"   label="Dashboard" />
        <NavIcon to="/analisa"     icon="analysis" label="Analisa" />

        <template v-if="isAdmin">
          <div class="h-px bg-gray-100 my-3 mx-1" />
          <p v-if="!sidebarCollapsed" class="px-3 pb-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest whitespace-nowrap">System</p>

          <NavIcon to="/etl-proses"             icon="etl"     label="ETL Proses" />
          <NavIcon to="/system/parameter"       icon="sliders" label="Parameter" />
          <NavIcon to="/system/notifications"   icon="bell"    label="Notifications" />
          <NavIcon to="/system/wa-config"       icon="chat"    label="WA Configuration" />
          <NavIcon to="/system/email-config"    icon="mail"    label="Email Configuration" />
          <NavIcon to="/system/access-tokens"   icon="shield"  label="Access Tokens" />
          <NavIcon to="/users"                  icon="users"   label="Users" />
          <NavIcon to="/roles"                  icon="shield"  label="Roles" />
        </template>
      </div>

      <!-- User footer -->
      <div class="border-t border-gray-100 flex-shrink-0" :class="sidebarCollapsed ? 'px-2 py-3' : 'px-3 py-3'">
        <div
          class="flex items-center rounded-lg"
          :class="sidebarCollapsed ? 'justify-center p-1' : 'gap-2 px-1.5 py-1.5'"
        >
          <!-- Klik area avatar+nama → buka modal profile (edit phone). -->
          <button
            type="button"
            class="flex items-center min-w-0 flex-1 rounded-lg hover:bg-gray-50 transition-colors text-left"
            :class="sidebarCollapsed ? 'justify-center p-1' : 'gap-2.5 px-1.5 py-1'"
            :title="sidebarCollapsed ? `${user?.name} — click to edit profile` : 'Edit profile (phone, password, etc.)'"
            @click="openProfileModal"
          >
            <div class="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold flex-shrink-0">
              {{ userInitial }}
            </div>
            <template v-if="!sidebarCollapsed">
              <div class="flex-1 min-w-0 overflow-hidden">
                <p class="text-xs font-semibold text-gray-800 truncate leading-tight">{{ user?.name }}</p>
                <p class="text-[11px] text-gray-400 truncate leading-tight mt-0.5">{{ userRolesLabel }}</p>
              </div>
            </template>
          </button>

          <button
            v-if="!sidebarCollapsed"
            class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors flex-shrink-0"
            title="Log out"
            @click="logout"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
            </svg>
          </button>
        </div>

        <button
          v-if="sidebarCollapsed"
          class="mt-1.5 w-full flex items-center justify-center h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors"
          title="Log out"
          @click="logout"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
          </svg>
        </button>
      </div>
    </aside>

    <!-- Main — saat pinned, sidebar hidden + main shift ke 60% layar kanan
         (window popup di 40% layar kiri). -->
    <div
      class="flex-1 flex flex-col min-h-screen min-w-0 transition-[margin-left,width] duration-200 ml-0"
      :class="pinnedFloatingWindow
        ? 'md:ml-[40vw] md:w-[60vw]'
        : (sidebarCollapsed ? 'md:ml-16' : 'md:ml-60')"
    >
      <header class="h-12 md:h-14 bg-white border-b border-gray-100 flex items-center px-3 md:px-6 sticky top-0 z-10 shadow-sm gap-2">
        <!-- Mobile: tombol logo + nama jadi toggle untuk sidebar drawer. Desktop sidebar selalu visible. -->
        <button
          type="button"
          class="md:hidden inline-flex items-center gap-2 -ml-1 px-1.5 py-1 rounded-lg hover:bg-gray-100 transition-colors"
          @click="sidebarOpenMobile = true"
        >
          <img src="/salestial-logo.png" alt="Salestial" class="w-7 h-7" />
        </button>
        <h1 class="text-sm font-semibold text-gray-800 truncate">{{ pageTitle }}</h1>
        <div class="ml-auto flex items-center gap-1">
          <PersonalNotes />
          <NotificationBell />
        </div>
      </header>

      <main class="flex-1 p-3 md:p-6 min-w-0 overflow-x-hidden">
        <slot />
      </main>
    </div>

    <!-- Profile modal — edit phone number untuk akun sendiri.
         Tidak butuh permission user.update; auth session sudah cukup. -->
    <Teleport to="body">
      <div v-if="profileModal.open" class="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" @click.self="closeProfileModal">
        <div class="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden max-h-[90vh] flex flex-col">
          <div class="px-5 py-4 border-b border-gray-100 flex items-start justify-between shrink-0">
            <div>
              <h3 class="text-sm font-bold text-gray-900">Profile</h3>
              <p class="text-[11px] text-gray-500 mt-0.5">Edit your account information</p>
            </div>
            <button class="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100" @click="closeProfileModal">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
            </button>
          </div>
          <div class="px-5 py-4 space-y-3 overflow-y-auto">
            <div>
              <label class="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Name</label>
              <p class="text-sm text-gray-800 truncate">{{ user?.name }}</p>
            </div>
            <div>
              <label class="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Email</label>
              <p class="text-sm text-gray-800 font-mono truncate">{{ user?.email }}</p>
            </div>
            <!-- Email alternatif — readonly; hanya admin yang bisa mengubah
                 (dipakai untuk matching login/SSO & tujuan email notifikasi). -->
            <div>
              <label class="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Alternate Email 1
                <span class="text-gray-400 normal-case font-medium tracking-normal">(readonly)</span>
              </label>
              <p class="text-sm font-mono truncate" :class="profileModal.emailAlt ? 'text-gray-800' : 'text-gray-400'">
                {{ profileModal.emailAlt || '—' }}
              </p>
            </div>
            <div>
              <label class="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">
                Alternate Email 2
                <span class="text-gray-400 normal-case font-medium tracking-normal">(readonly)</span>
              </label>
              <p class="text-sm font-mono truncate" :class="profileModal.emailAlt2 ? 'text-gray-800' : 'text-gray-400'">
                {{ profileModal.emailAlt2 || '—' }}
              </p>
            </div>
            <p class="text-[10px] text-gray-400">Alternate emails are used for login (password/SSO) and as email notification targets. Contact an admin to change them.</p>
            <div class="pt-2 border-t border-gray-100">
              <label class="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Phone (WhatsApp)</label>
              <input
                v-model="profileModal.phone"
                type="tel"
                inputmode="tel"
                placeholder="+6281234567890"
                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg font-mono focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
                @input="onProfilePhoneInput"
              />
              <p class="text-[10px] text-gray-400 mt-1.5">Used for WhatsApp notifications. E.164 format (leading <span class="font-mono">+</span> + country code).</p>
            </div>

            <!-- Ganti password — collapsible supaya modal tetap ringkas.
                 Akun SSO-only (hasPassword=false) tidak diminta password lama. -->
            <div class="pt-2 border-t border-gray-100">
              <button
                class="w-full flex items-center justify-between text-left"
                @click="profileModal.showPassword = !profileModal.showPassword"
              >
                <span class="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                  {{ profileModal.hasPassword ? 'Change Password' : 'Create Password' }}
                </span>
                <svg
                  class="w-4 h-4 text-gray-400 transition-transform"
                  :class="profileModal.showPassword ? 'rotate-180' : ''"
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"/>
                </svg>
              </button>

              <div v-if="profileModal.showPassword" class="mt-2 space-y-2">
                <p v-if="!profileModal.hasPassword" class="text-[10px] text-amber-700 bg-amber-50 px-3 py-2 rounded-lg border border-amber-100">
                  Your account has no local password yet (you sign in with Google). Create one here if you also want to sign in with email + password.
                </p>
                <div v-if="profileModal.hasPassword">
                  <label class="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Current Password</label>
                  <input
                    v-model="profileModal.currentPassword"
                    type="password"
                    autocomplete="current-password"
                    placeholder="••••••"
                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
                  />
                </div>
                <div>
                  <label class="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">New Password</label>
                  <input
                    v-model="profileModal.newPassword"
                    type="password"
                    autocomplete="new-password"
                    placeholder="At least 6 characters"
                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
                  />
                </div>
                <div>
                  <label class="block text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-1">Confirm New Password</label>
                  <input
                    v-model="profileModal.confirmPassword"
                    type="password"
                    autocomplete="new-password"
                    placeholder="Repeat the new password"
                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
                  />
                </div>
                <p class="text-[10px] text-gray-400">Leave every password field empty if you don't want to change it.</p>
              </div>
            </div>

            <p v-if="profileModal.error" class="text-xs text-red-700 bg-red-50 px-3 py-2 rounded-lg border border-red-100">{{ profileModal.error }}</p>
            <p v-if="profileModal.success" class="text-xs text-green-700 bg-green-50 px-3 py-2 rounded-lg border border-green-100">{{ profileModal.success }}</p>
          </div>
          <div class="px-5 py-3 bg-gray-50 border-t border-gray-100 flex justify-end gap-2 shrink-0">
            <button class="px-4 py-2 text-xs font-semibold text-gray-600 hover:bg-gray-200 rounded-lg" @click="closeProfileModal">Cancel</button>
            <button class="px-4 py-2 text-xs font-semibold bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50" :disabled="profileModal.saving" @click="saveProfile">
              {{ profileModal.saving ? 'Saving…' : 'Save' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    <ConfirmDialog />
  </div>
</template>

<script setup lang="ts">
const route = useRoute()
const { user, fetch: refreshSession } = useUserSession()
const { isAdmin } = usePermissions()

// Embedded mode → render minimal chrome (no sidebar, no header). Diaktifkan
// via query `?embedded=1` saat halaman dimuat di iframe popup.
const embedded = computed(() => String(route.query.embedded ?? '') === '1')

// Pinned floating window state — di-set dari halaman Production Planning saat
// user pin window Simulation / Inventory. Layout merespons dengan:
//   - hide sidebar
//   - shift main content ke half-screen kanan (50vw padding kiri)
const pinnedFloatingWindow = useState<boolean>('pinned-floating-window', () => false)

const sidebarCollapsed = ref(false)
// Mobile drawer state — terpisah dari desktop collapse. Default tertutup; dibuka
// via tombol logo di header dan ditutup otomatis saat user pindah halaman.
const sidebarOpenMobile = ref(false)

onMounted(() => {
  const saved = localStorage.getItem('bi-sidebar-collapsed')
  if (saved !== null) sidebarCollapsed.value = saved === 'true'
})

watch(sidebarCollapsed, val => {
  localStorage.setItem('bi-sidebar-collapsed', String(val))
})

// Auto-close mobile drawer saat route berubah (klik nav item).
watch(() => route.fullPath, () => { sidebarOpenMobile.value = false })

provide('sidebarCollapsed', sidebarCollapsed)
provide('expandSidebar', () => { sidebarCollapsed.value = false })

// Saat window simulation/inventory di-pin, toggle class di <body> supaya semua
// modal backdrop yang pakai `fixed inset-0 ... bg-black/40` ikut menyesuaikan
// (left: 40vw) dan tidak ketutup oleh window popup di kiri layar. CSS rule
// ada di <style> non-scoped di bawah.
watch(pinnedFloatingWindow, (v) => {
  if (typeof document === 'undefined') return
  document.body.classList.toggle('has-pinned-window', !!v)
}, { immediate: true })

const pageTitleMap: Record<string, string> = {
  '/dashboard':             'Dashboard',
  '/analisa':               'Analisa',
  '/etl-proses':            'ETL Proses',
  '/system/parameter':      'Parameter',
  '/system/notifications':  'Notifications',
  '/system/wa-config':      'WA Configuration',
  '/system/email-config':   'Email Configuration',
  '/system/access-tokens':  'Access Tokens',
  '/users':                 'Users',
  '/roles':                 'Roles & Permissions',
}

// Satu modul saja — tidak ada pergantian branding seperti FMS/PPIC di izi_erp.
// Nama produk yang dipakai ke user: Salestial (logo & palet dari materi
// Salestial, lihat /opt/development/izi_salestial_demo).
const moduleLabel = { short: 'Salestial', long: 'Dashboard' }

// Judul halaman detail diisi oleh halamannya sendiri lewat state ini, karena
// namanya (nama dashboard / analisa / tabel) baru diketahui setelah data dimuat.
const detailTitle = useState<string>('page-detail-title', () => '')
watch(() => route.fullPath, () => { detailTitle.value = '' })

const pageTitle = computed(() => {
  if (route.path === '/etl-proses/connections') return 'ETL — Database Connections'
  if (route.path.startsWith('/etl-proses/'))    return 'ETL Proses — Detail'
  if (detailTitle.value) return detailTitle.value
  if (route.path.startsWith('/dashboard/'))     return 'Dashboard'
  if (route.path.startsWith('/analisa/'))       return 'Analisa'
  return pageTitleMap[route.path] ?? 'Salestial Dashboard'
})
const userInitial = computed(() => user.value?.name?.charAt(0)?.toUpperCase() ?? 'U')
const userRolesLabel = computed(() => {
  const u = user.value as any
  const rs = Array.isArray(u?.roles) ? u.roles : []
  return rs.length ? rs.join(', ') : '—'
})

async function logout() {
  await $fetch('/api/auth/logout', { method: 'POST' })
  await refreshSession()
  // Clear user-access state (roles + permissions di-load terpisah dari session).
  const access = useState<{ roles: string[]; permissions: string[] }>('user-access')
  access.value = { roles: [], permissions: [] }
  navigateTo('/login')
}

// ── Profile modal ──────────────────────────────────────────────────────────
const profileModal = reactive({
  open: false,
  phone: '',
  // Readonly info, di-load dari /api/auth/me (tidak ada di session cookie).
  emailAlt: '',
  emailAlt2: '',
  // Ganti password
  hasPassword: true,
  showPassword: false,
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
  saving: false,
  error: '',
  success: '',
})

function normalizePhoneClient(input: string): string {
  if (!input) return ''
  let s = input.replace(/[^0-9+]/g, '')
  if (!s) return ''
  const hasPlus = s.startsWith('+')
  s = s.replace(/\+/g, '')
  if (!s) return ''
  if (hasPlus)               return '+' + s
  if (s.startsWith('0'))     return '+62' + s.slice(1)
  if (s.startsWith('62'))    return '+' + s
  return '+' + s
}

async function openProfileModal() {
  profileModal.error = ''
  profileModal.success = ''
  profileModal.saving = false
  resetProfilePasswordFields()
  profileModal.showPassword = false
  // Tutup mobile drawer sambil buka modal supaya tidak overlap.
  sidebarOpenMobile.value = false
  // Pre-fill langsung dari session (instant). Lalu refresh dari /api/auth/me
  // di background supaya kalau ada update di tab lain ikut ke-pick-up.
  // emailAlt/emailAlt2 + hasPassword hanya ada di /api/auth/me (session cookie
  // sengaja dijaga minimal), jadi selalu di-load dari sana.
  profileModal.phone = (user.value as any)?.phoneNumber ?? ''
  profileModal.emailAlt = ''
  profileModal.emailAlt2 = ''
  profileModal.open = true
  try {
    const me = await $fetch<{
      phoneNumber?: string
      emailAlt?: string
      emailAlt2?: string
      hasPassword?: boolean
    }>('/api/auth/me')
    if (me?.phoneNumber !== undefined) profileModal.phone = me.phoneNumber
    profileModal.emailAlt  = me?.emailAlt  ?? ''
    profileModal.emailAlt2 = me?.emailAlt2 ?? ''
    profileModal.hasPassword = me?.hasPassword !== false
  } catch { /* ignore — session value already shown */ }
}

function resetProfilePasswordFields() {
  profileModal.currentPassword = ''
  profileModal.newPassword = ''
  profileModal.confirmPassword = ''
}

function closeProfileModal() {
  if (profileModal.saving) return
  profileModal.open = false
  resetProfilePasswordFields()
}

function onProfilePhoneInput(e: Event) {
  const t = e.target as HTMLInputElement
  const cleaned = normalizePhoneClient(t.value)
  if (cleaned !== t.value) {
    profileModal.phone = cleaned
    t.value = cleaned
  }
}

async function saveProfile() {
  if (profileModal.saving) return
  profileModal.error = ''
  profileModal.success = ''

  // Validasi password di client dulu supaya feedback-nya instan. Password
  // hanya dikirim kalau user memang mengisi field password baru.
  const wantPasswordChange = !!(profileModal.newPassword || profileModal.confirmPassword)
  if (wantPasswordChange) {
    if (profileModal.hasPassword && !profileModal.currentPassword) {
      profileModal.showPassword = true
      profileModal.error = 'Current password is required'
      return
    }
    if (profileModal.newPassword.length < 6) {
      profileModal.showPassword = true
      profileModal.error = 'New password must be at least 6 characters'
      return
    }
    if (profileModal.newPassword !== profileModal.confirmPassword) {
      profileModal.showPassword = true
      profileModal.error = 'Password confirmation does not match'
      return
    }
  }

  profileModal.saving = true
  try {
    await $fetch('/api/auth/profile', {
      method: 'PUT',
      body: {
        phoneNumber: profileModal.phone,
        ...(wantPasswordChange
          ? {
              currentPassword: profileModal.currentPassword,
              newPassword: profileModal.newPassword,
            }
          : {}),
      },
    })
    await refreshSession()
    if (wantPasswordChange) {
      // Jangan langsung tutup — kasih konfirmasi bahwa password sudah berganti.
      profileModal.hasPassword = true
      resetProfilePasswordFields()
      profileModal.showPassword = false
      profileModal.success = 'Changes saved. Your password has been changed.'
    } else {
      profileModal.open = false
    }
  } catch (e: any) {
    profileModal.error = e?.data?.message ?? e?.message ?? 'Failed to save'
  } finally {
    profileModal.saving = false
  }
}
</script>

<style>
/* Saat window popup (Simulation / Inventory) di-pin ke 40% kiri layar, modal
   backdrop yang pakai `fixed inset-0 ... bg-black/40 backdrop-blur-sm`
   digeser kanan (left: 40vw) supaya tidak tertutup popup window.
   Hanya target div backdrop yang punya bg-black/40 — biar tidak nyentol
   sticky notes / floating windows yang punya positioning sendiri.
   Mobile (< 768px): tetap full-screen (popup window juga akan hidden). */
@media (min-width: 768px) {
  body.has-pinned-window .fixed.inset-0.bg-black\/40 {
    left: 40vw !important;
  }
}
</style>
